import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { reloadApp } from "../shared/utils";
import { getLocalStorage, removeLocalStorage, setLocalStorage } from "../shared/utils/localStorage";
import { setSessionStorage } from "../shared/utils/sessionStorage";
import storageConstants from "../shared/constants/storage";
import { setReloadApp, showToastNotification, updateFeatureToggles } from "../App/state/actions.js";
import PropTypes from "prop-types";
import { Event } from "@harnessio/ff-javascript-client-sdk";

const { PREV_IN_PANIC_STATE, USER_HAS_LOGGED_IN } = storageConstants;

const ECEManager = (props) => {
  const {
    appProvider,
    videoPlaying,
    setReloadApp,
    showToastNotification,
    updateFeatureToggles,
    harnessOnFunc,
    userProfile,
  } = props;
  const panicModeState = useRef(appProvider.panicMode);
  const videoState = useRef(false);
  const appWillReload = useRef(false);
  const hasUserSeenEnabledToast = useRef(false);
  const { t: translate } = useTranslation();
  const panicModeConstants = appProvider.config.PANIC_MODE;

  const safeMap = useMemo(() => {
    const map = Object.create(null);
    map["panic_vod_playback"] = "isVODDisabled";
    map["panic_live_playback"] = "isLIVEDisabled";
    map["panic_fast_playback"] = "isFASTDisabled";
    map["panic_restart_playback"] = "isRestartDisabled";
    map["panic_catchup_playback"] = "isCatchUpDisabled";
    map["panic_cpvr_playback"] = "isCPVRDisabled";
    return map;
  }, []);

  /**
   * Displays a contextual service toast notification if appropriate
   *
   * @param {Boolean} isPanicMode - Flag indicating if panic mode is enabled
   */
  const handleToastNotification = useCallback(
    (isPanicMode) => {
      const wasPrevInPanic = getLocalStorage(PREV_IN_PANIC_STATE);
      if (isPanicMode && !hasUserSeenEnabledToast.current) {
        showToastNotification(translate("panic_mode_enabled"));
        setLocalStorage(PREV_IN_PANIC_STATE, true, panicModeConstants.PREV_PANIC_MODE_STORAGE_TTL_MS);
        hasUserSeenEnabledToast.current = true;
      } else if (!isPanicMode && wasPrevInPanic) {
        setLocalStorage(USER_HAS_LOGGED_IN, false);
        showToastNotification(translate("panic_mode_restored"));
        removeLocalStorage(PREV_IN_PANIC_STATE);
        removeLocalStorage(USER_HAS_LOGGED_IN);
      }
    },
    [panicModeConstants.PREV_PANIC_MODE_STORAGE_TTL_MS, showToastNotification, translate]
  );

  const processVideoPanicState = useCallback(
    (panicFlag) => {
      return safeMap[panicFlag.flag]
        ? { [safeMap[panicFlag.flag]]: panicFlag.value === "enabled" ?? false }
        : undefined;
    },
    [safeMap]
  );

  /**
   * Purpose of this method is to handle refreshing the app from a panic mode and leaving panic mode
   */
  const handleReloadApp = useCallback(async () => {
    if (videoState.current) {
      appWillReload.current = true;
      setReloadApp(true);
    } else {
      reloadApp(appProvider);
    }
  }, [appProvider, setReloadApp]);

  useEffect(() => {
    if (userProfile.isLoggedIn && !videoPlaying.playing && !appWillReload.current)
      handleToastNotification(panicModeState.current);
  }, [handleToastNotification, videoPlaying.playing, userProfile]);

  // Used to detect flag changes in harness
  useEffect(() => {
    harnessOnFunc(Event.CHANGED, (flagInfo) => {
      if (flagInfo.flag === "panic") {
        let flagObj;
        try {
          flagObj = JSON.parse(flagInfo.value);
        } catch (e) {
          console.error("[ECEManager] Failed to parse panic flag value:", e);
          return;
        }
        const panicState = flagObj.state;
        if (
          (panicState === "full" && !panicModeState.current) ||
          ((panicState === "none" || panicState === "legacy") && panicModeState.current)
        ) {
          handleReloadApp();
        } else if (panicState === "partial") {
          panicModeState.current = panicState;
        }
      } else if (flagInfo.flag.includes("panic_")) {
        const updatedFF = processVideoPanicState(flagInfo);
        if (updatedFF) {
          updateFeatureToggles(updatedFF);
        }
      }
    });
  }, [harnessOnFunc, handleReloadApp, updateFeatureToggles, processVideoPanicState]);

  return <></>;
};

function mapStateToProps({ app }) {
  return {
    videoPlaying: app.videoPlaying,
    appProvider: app.provider,
    userProfile: app.userProfile,
  };
}

const mapDispatchToProps = {
  setReloadApp,
  showToastNotification,
  updateFeatureToggles,
};

ECEManager.propTypes = {
  userProfile: PropTypes.object,
  appProvider: PropTypes.object,
  videoPlaying: PropTypes.object,
  setReloadApp: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(ECEManager);
